import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PanelComponent } from '../../../../../osp-ui/components/panel/panel/panel.component';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { LogoImagePipe } from '../../../../../core/components/layout/sidebar/pipes/logo-image.pipe';
import { PublicFooterComponent } from '../../../../../shared/components/public-footer/public-footer.component';
import { OspTemplateDirective } from '../../../../../osp-ui/directives/osp-template.directive';
import { ContentLoadingContainerComponent } from '../../../../../shared/components/content-loading-container/content-loading-container.component';
import { MapRefreshButtonService } from '../../../../../shared/components/maps/components/custom-controls/map-refresh-control/map-refresh-button.service';
import { ThemeService } from '../../../../../whitelabel/services/theme.service';
import { LanguageService } from '../../../../../core/services/language.service';
import { ActivatedRoute } from '@angular/router';
import { ContactValidationDatasourceService } from '../../services/contact-validation-datasource.service';
import { WhitelabelManagerService } from '../../../../../whitelabel/services/whitelabel-manager.service';

@Component({
  selector: 'osp-contact-mail-validation-page',
  standalone: true,
  imports: [
    PanelComponent,
    TranslateModule,
    AsyncPipe,
    LogoImagePipe,
    PublicFooterComponent,
    OspTemplateDirective,
    ContentLoadingContainerComponent,
  ],
  templateUrl: './contact-mail-validation.component.html',
  styleUrl: './contact-mail-validation.component.scss',
  providers: [MapRefreshButtonService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactMailValidationComponent implements OnInit {
  protected themeService = inject(ThemeService);
  protected whitelabelManager = inject(WhitelabelManagerService);
  protected languageService = inject(LanguageService);
  private readonly datasource = inject(ContactValidationDatasourceService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected $isLoading = computed(() => {
    return this.datasource.$isLoading();
  });
  protected $isSuccess = signal(false);
  protected $isValidation: WritableSignal<boolean> = signal(false);

  ngOnInit(): void {
    this.themeService.applyTheme(this.whitelabelManager.getDefaultTheme());
    this.languageService.setDefaultLanguage(this.languageService.getBrowserLanguage());
    this.$isValidation.set(this.activatedRoute.snapshot.url.some((segment) => segment.path === 'validate'));

    const code: string = this.activatedRoute.snapshot.paramMap.get('code')!;
    if (this.$isValidation()) {
      this.validate(code);
    } else {
      this.revoke(code);
    }
  }

  private validate(code: string): void {
    this.datasource
      .validateMailAddress(code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.$isSuccess.set(true),
        error: () => this.$isSuccess.set(false),
      });
  }

  private revoke(code: string): void {
    this.datasource
      .revokeMailAddress(code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.$isSuccess.set(true),
        error: () => this.$isSuccess.set(false),
      });
  }
}
